import {
  IAcceptPartnerAgreementParams,
  ICursorPageParams,
  IPartnerMeResponse,
  IPartnerReferralsResponse,
  IPartnerStatementsResponse,
  IReferralPaymentsResponse,
  ISendPartnerInvitesParams,
  ISendPartnerInvitesResponse,
} from "types/partner/IPartnerTypes";

import { API_ENDPOINTS } from "../configs/apiConstants";
import { appendQueryString } from "../utils/partner/queryStringUtil";

import { BaseApiService } from "./BaseApiService";

/**
 * Calls the signed-in referral partner's own endpoints.
 */
export class PartnerService extends BaseApiService {
  /**
   * Reads the signed-in partner's record. A 404 means the user is not a partner.
   */
  static async getMeAsync({ authToken }: { authToken: string }): Promise<IPartnerMeResponse> {
    return this.getAsync<IPartnerMeResponse>(API_ENDPOINTS.PARTNER.ME, authToken);
  }

  /**
   * Records acceptance of the given agreement version.
   */
  static async acceptAgreementAsync({
    payload,
    authToken,
  }: {
    payload: IAcceptPartnerAgreementParams;
    authToken: string;
  }): Promise<IPartnerMeResponse> {
    return this.postAsync<IPartnerMeResponse>(API_ENDPOINTS.PARTNER.AGREEMENT, authToken, payload);
  }

  /**
   * Sends referral invites. Fails with a 409 `AGREEMENT_NOT_ACCEPTED` until the agreement is accepted.
   */
  static async sendInvitesAsync({
    payload,
    authToken,
  }: {
    payload: ISendPartnerInvitesParams;
    authToken: string;
  }): Promise<ISendPartnerInvitesResponse> {
    return this.postAsync<ISendPartnerInvitesResponse>(API_ENDPOINTS.PARTNER.INVITES, authToken, payload);
  }

  /**
   * Lists the partner's referrals, one cursor page at a time.
   */
  static async listReferralsAsync({
    page,
    authToken,
  }: {
    page: ICursorPageParams;
    authToken: string;
  }): Promise<IPartnerReferralsResponse> {
    const endpoint = appendQueryString({ endpoint: API_ENDPOINTS.PARTNER.REFERRALS, query: { ...page } });
    return this.getAsync<IPartnerReferralsResponse>(endpoint, authToken);
  }

  /**
   * Lists the payments collected from one referred customer.
   */
  static async listReferralPaymentsAsync({
    referralId,
    authToken,
  }: {
    referralId: string;
    authToken: string;
  }): Promise<IReferralPaymentsResponse> {
    return this.getAsync<IReferralPaymentsResponse>(API_ENDPOINTS.PARTNER.REFERRAL_PAYMENTS(referralId), authToken);
  }

  /**
   * Lists the partner's monthly statements, one cursor page at a time.
   */
  static async listStatementsAsync({
    page,
    authToken,
  }: {
    page: ICursorPageParams;
    authToken: string;
  }): Promise<IPartnerStatementsResponse> {
    const endpoint = appendQueryString({ endpoint: API_ENDPOINTS.PARTNER.STATEMENTS, query: { ...page } });
    return this.getAsync<IPartnerStatementsResponse>(endpoint, authToken);
  }
}
